import { supabase } from './supabase';
import { upsertCmsKey, getAdminEmail } from './cmsStore';

/**
 * Public, non-secret analytics / verification settings live in
 * `public.cms_content` under the key `public_settings`, which is publicly
 * readable. That lets EVERY visitor load the analytics scripts and meta
 * verification tags without exposing anything sensitive.
 */
export const PUBLIC_SETTINGS_KEY = 'public_settings';

export interface PublicSettings {
  ga4Id?: string;
  gtmId?: string;
  gscVerification?: string;
  bingVerification?: string;
  clarityId?: string;
  linkedinPartnerId?: string;
  metaPixelId?: string;
  teamsWebhook?: string;
  /** Cloudflare Turnstile SITE key (public) for the form bot check. */
  turnstileSiteKey?: string;
  /** Cloudflare Stream customer subdomain (non-secret) so public pages can
   *  build player/thumbnail URLs without reading admin-only settings. */
  cloudflareStreamSubdomain?: string;
  /** Public Hire-Experts CTA settings (sticky bar + A/B), mirrored from
   *  admin_settings by the hire-cta-settings function. */
  hire?: {
    stickyEnabled?: boolean;
    stickyGroups?: string[];
    stickyLabel?: string;
    stickyPhone?: boolean;
    abEnabled?: boolean;
  };
  /** Growth Intelligence: consent mode ('notice_only' default | 'opt_in'). */
  giConsentMode?: 'notice_only' | 'opt_in';
  /** Growth Intelligence: extra non-production hosts to exclude from analytics. */
  giExcludedHosts?: string[];
}

/** Loads the public settings row. Missing row → empty object. */
export async function loadPublicSettings(): Promise<PublicSettings> {
  try {
    const { data, error } = await supabase
      .from('cms_content')
      .select('value')
      .eq('key', PUBLIC_SETTINGS_KEY)
      .maybeSingle();
    if (error || !data) return {};
    return (data.value as PublicSettings) || {};
  } catch {
    return {};
  }
}

/** Admin-only: merges a patch into `public_settings` and persists it. */
export async function savePublicSettings(patch: Partial<PublicSettings>): Promise<void> {
  const email = await getAdminEmail();
  const current = await loadPublicSettings();
  const merged: PublicSettings = { ...current };
  (Object.keys(patch) as (keyof PublicSettings)[]).forEach((k) => {
    const v = patch[k];
    if (v === undefined) return;
    if (String(v).trim() === '') {
      delete merged[k];
    } else {
      merged[k] = String(v).trim();
    }
  });
  await upsertCmsKey(PUBLIC_SETTINGS_KEY, merged, email);
}

/** Loads the Cloudflare Stream customer subdomain from public settings. */
export async function loadStreamSubdomain(): Promise<string | undefined> {
  const settings = await loadPublicSettings();
  const sub = (settings.cloudflareStreamSubdomain || '').trim();
  return sub || undefined;
}

/**
 * Fires a conversion event across whichever analytics providers are loaded.
 * Safe to call anywhere — silently no-ops when nothing is configured.
 */
export function trackConversion(
  eventName: string,
  params: Record<string, unknown> = {},
): void {
  try {
    const w = window as unknown as {
      gtag?: (...args: unknown[]) => void;
      dataLayer?: unknown[];
      fbq?: (...args: unknown[]) => void;
      lintrk?: (...args: unknown[]) => void;
    };
    if (typeof w.gtag === 'function') w.gtag('event', eventName, params);
    if (Array.isArray(w.dataLayer)) w.dataLayer.push({ event: eventName, ...params });
    if (typeof w.fbq === 'function') w.fbq('trackCustom', eventName, params);
    if (typeof w.lintrk === 'function') w.lintrk('track', { conversion_id: eventName });
  } catch {
    /* analytics should never break the app */
  }
}